import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import FilterBar from './components/FilterBar';
import KPICard from './components/KPICard';
import ReportChart from './components/ReportChart';
import ReportAnalysis from './components/ReportAnalysis';
import ExportButton from './components/ExportButton';
import { getBillingReports } from '../../../services/reportService';
import { CHART_COLORS } from './utils/chartConfig';
import { prepareKPIsForExport } from './utils/exportUtils';
import './Reports.css';

const BillingReports = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({});
    const [reportData, setReportData] = useState(null);

    const fetchReportData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getBillingReports(filters);
            setReportData(response.data);
        } catch (err) {
            console.error('Error fetching billing reports:', err);
            setError('Erreur lors du chargement des données. Veuillez réessayer.');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    const invoiceStatusData = reportData?.invoiceStatus ? {
        labels: ['Brouillon', 'Finalisées', 'Payées'],
        datasets: [{
            data: [
                reportData.invoiceStatus.draft,
                reportData.invoiceStatus.finalized,
                reportData.invoiceStatus.paid
            ],
            backgroundColor: [CHART_COLORS.warning, CHART_COLORS.info, CHART_COLORS.success],
            borderWidth: 0
        }]
    } : null;

    const revenueByProductData = reportData?.revenueByProduct ? {
        labels: reportData.revenueByProduct.map(item => item.productName),
        datasets: [{
            label: 'Chiffre d\'affaires',
            data: reportData.revenueByProduct.map(item => item.revenue),
            backgroundColor: CHART_COLORS.series,
            borderRadius: 8
        }]
    } : null;

    const monthlyRevenueData = reportData?.monthlyRevenue ? {
        labels: reportData.monthlyRevenue.map(item => item.month),
        datasets: [{
            label: 'CA facturé',
            data: reportData.monthlyRevenue.map(item => item.revenue),
            backgroundColor: CHART_COLORS.primary,
            borderRadius: 8
        }]
    } : null;

    const exportData = reportData ? [
        ...prepareKPIsForExport(reportData.kpis),
        { Indicateur: '', Valeur: '' },
        { Indicateur: 'CA Mensuel', Valeur: '' },
        ...(reportData.monthlyRevenue || []).map(item => ({ Indicateur: item.month, Valeur: item.revenue })),
        { Indicateur: '', Valeur: '' },
        { Indicateur: 'CA par Produit', Valeur: '' },
        ...(reportData.revenueByProduct || []).map(item => ({ Indicateur: item.productName, Valeur: item.revenue }))
    ] : null;

    return (
        <DashboardLayout>
            <div className="reports-page">
                <div className="reports-header">
                    <div>
                        <h1 className="reports-title">Rapports & Analytics</h1>
                        <p className="reports-subtitle">Rapports facturation</p>
                    </div>
                    <ExportButton
                        reportName="rapports_facturation"
                        data={exportData}
                        headers={['Indicateur', 'Valeur']}
                        keys={['Indicateur', 'Valeur']}
                        disabled={loading || !reportData}
                    />
                </div>

                <FilterBar
                    onFilterChange={setFilters}
                    availableFilters={['currency', 'period', 'client', 'product']}
                />

                {error && (
                    <div className="alert alert-danger rounded-3 border-0 shadow-sm">
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        {error}
                    </div>
                )}

                {/* Analysis Section */}
                {reportData?.analysis && (
                    <ReportAnalysis analysis={reportData.analysis} />
                )}

                <div className="row g-4 mb-4">
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="CA facturé"
                            value={reportData?.kpis?.invoicedRevenue}
                            previousValue={reportData?.kpis?.previousInvoicedRevenue}
                            format="currency"
                            currency={filters.currency || 'EUR'}
                            icon="bi bi-receipt"
                            gradient="blue"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Factures en attente"
                            value={reportData?.kpis?.pendingInvoices}
                            format="number"
                            icon="bi bi-hourglass-split"
                            gradient="orange"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Factures payées"
                            value={reportData?.kpis?.paidInvoices}
                            format="number"
                            icon="bi bi-check-circle"
                            gradient="green"
                            loading={loading}
                        />
                    </div>
                    <div className="col-xl-3 col-md-6">
                        <KPICard
                            title="Retards de paiement"
                            value={reportData?.kpis?.paymentDelays}
                            format="number"
                            icon="bi bi-exclamation-triangle"
                            gradient="red"
                            loading={loading}
                        />
                    </div>
                </div>

                <div className="row g-4 mb-4">
                    <div className="col-lg-4">
                        <ReportChart
                            type="doughnut"
                            data={invoiceStatusData}
                            title="Statut des factures"
                            height={300}
                            loading={loading}
                            id="invoice-status-chart"
                        />
                    </div>
                    <div className="col-lg-8">
                        <ReportChart
                            type="bar"
                            data={monthlyRevenueData}
                            title="Chiffre d'affaires facturé par mois"
                            height={300}
                            loading={loading}
                        />
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-12">
                        <ReportChart
                            type="bar"
                            data={revenueByProductData}
                            title="Chiffre d'affaires par produit de facturation"
                            height={300}
                            loading={loading}
                        />
                    </div>
                </div>

                {/* Top 15 Clients with Payment Delays */}
                {reportData?.paymentDelayClients && reportData.paymentDelayClients.length > 0 && (
                    <div className="row g-4 mt-2">
                        <div className="col-12">
                            <div className="card border-0 shadow-sm">
                                <div className="card-header bg-white border-0 py-3">
                                    <h5 className="mb-0 fw-bold">Top 15 clients avec retards de paiement</h5>
                                </div>
                                <div className="card-body p-0">
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0">
                                            <thead>
                                                <tr>
                                                    <th>Client</th>
                                                    <th>Montant dû</th>
                                                    <th>Jours de retard</th>
                                                    <th>Nombre de factures</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {reportData.paymentDelayClients.slice(0, 15).map((client, index) => (
                                                    <tr key={index}>
                                                        <td className="fw-medium">{client.clientName}</td>
                                                        <td>{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: filters.currency || 'EUR' }).format(client.amountDue)}</td>
                                                        <td>
                                                            <span className={`badge ${client.daysLate > 30 ? 'bg-danger' : 'bg-warning'}`}>
                                                                {client.daysLate} jours
                                                            </span>
                                                        </td>
                                                        <td>{client.invoiceCount}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default BillingReports;
